import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, finalize, firstValueFrom, Subscription } from 'rxjs';
import { NotificacionPushToken } from 'src/app/models/NotificationPushToken';
import { NotificacionService } from 'src/app/services/notificacion.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { PushNotifications } from '@capacitor/push-notifications';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { IS_NATIVE } from './../../shared/platform.util';

type PermissionStatus = 'granted' | 'denied' | 'prompt';

@Component({
    selector: 'app-notificaciones',
    templateUrl: './notificaciones.component.html',
    styleUrls: ['./notificaciones.component.css'],
    standalone: false
})
export class MoreNotificacionesComponent {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private _notificacionService = inject(NotificacionService);
  private router = inject(Router);
  private _location = inject(Location);

  // estado
  error   = signal<string>('');
  message = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  dispositivos: NotificacionPushToken[] = [];
  estadoNotificaciones = new BehaviorSubject<PermissionStatus>('prompt');
  showNotifications = true;
  
  private subs = new Subscription();

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    try {
      await this._loadingService.run(async () => {
        await this._authService.ensureSession();
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message ?? 'No se pudo cargar tu cuenta.');
      this.router.navigate(['/sign-in']);
      return;
    }

    // UI: flag rojo de notificaciones en navbar (si lo usan)
    this.subs.add(
      this._notificacionService.showNotifications$.subscribe(v => this.showNotifications = v)
    );

    // Permisos iniciales
    await this.refreshPermissionStatus();

    // Cargar dispositivos del usuario
    await this.getDispositivosDelUsuario();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ====== Permisos de notificación (web/nativo) ======
  private async refreshPermissionStatus(): Promise<void> {
    if (IS_NATIVE) {
      try {
        const p = await PushNotifications.checkPermissions();
        // p.receive: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
        const mapped: PermissionStatus =
          p.receive === 'granted' ? 'granted' :
          p.receive === 'denied'  ? 'denied'  :
          'prompt'; // incluye 'prompt-with-rationale'
        this.estadoNotificaciones.next(mapped);
      } catch {
        this.estadoNotificaciones.next('denied');
      }
    } else {
      // Notification.permission: 'default' | 'denied' | 'granted'
      const perm: NotificationPermission =
      typeof Notification === 'undefined' ? 'denied' : Notification.permission;

      const mapped: PermissionStatus =
        perm === 'granted' ? 'granted' :
        perm === 'denied'  ? 'denied'  :
        'prompt'; // mapea 'default' -> 'prompt'

      this.estadoNotificaciones.next(mapped);
    }
  }

  // ====== Cargar dispositivos del usuario ======
  async getDispositivosDelUsuario(): Promise<void> {
    const uid = this._usuarioService.usuario()?.id;
    if (!uid) return;

    await this._loadingService.run(async () => {
      await firstValueFrom(
        this._notificacionService.getNotificationesPushToken(uid).pipe(
          finalize(() => {})
        )
      ).then(res => {
        this.dispositivos = res ?? [];
        if (this.dispositivos.length === 0) {
          // Si no hay dispositivos, sugerí habilitar notificaciones
          this._notificacionService.setShowNotifications(true);
        }
      }).catch(e => {
        console.error('Error al obtener las Notificaciones Push Token del usuario', e);
        this.error.set('Error al obtener los dispositivos de notificación');
      });
    });
  }

  // ====== Marcar dispositivo "activo" (match con storage) ======
  isActiveDispositivo(id: number): boolean {
    const disp = this.dispositivos.find(d => d.id === id);
    const tokenNotiPush = localStorage.getItem('tokenNotiPush') ? JSON.parse(localStorage.getItem('tokenNotiPush')!) : null;
    const tokenFcm      = localStorage.getItem('tokenFcmPush') ?? null;
    const uid           = this._usuarioService.usuario()?.id;

    if (!disp || !uid) return false;

    const matchWeb =
      tokenNotiPush &&
      tokenNotiPush.endpoint === disp.x_webpush_endpoint &&
      tokenNotiPush.keys?.auth  === disp.x_webpush_keys_auth &&
      tokenNotiPush.keys?.p256dh === disp.x_webpush_keys_p256dh;

    const matchFcm = tokenFcm && tokenFcm === disp.x_fcm_token;

    return uid === disp.id_usuario && (matchWeb || !!matchFcm);
  }

  // ====== Enviar notificación de prueba ======
  async enviarPrueba(id: number): Promise<void> {
    const disp = this.dispositivos.find(d => d.id === id);
    if (!disp) {
      this.error.set('No se encontró el dispositivo para enviar la prueba.');
      return;
    }

    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(this._notificacionService.sendPushPrueba(disp));
        this.message.set('Notificación de prueba enviada correctamente');
        setTimeout(() => this.message.set(''), 2500);
      } catch (e) {
        console.error(e);
        this.error.set('Error al enviar la notificación de prueba');
      }
    });
  }

  // ====== Eliminar dispositivo ======
  async eliminarDispositivo(id: number): Promise<void> {
    const disp = this.dispositivos.find(d => d.id === id);
    // Optimistic UI
    this.dispositivos = this.dispositivos.filter(d => d.id !== id);

    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(this._notificacionService.deleteNotificacionPushToken(id));

        // Si eliminaste el dispositivo “actual” en nativo → desregistrar
        if (disp && disp.x_fcm_token === localStorage.getItem('tokenFcmPush')) {
          try { await PushNotifications.unregister(); } catch {}
          localStorage.removeItem('tokenFcmPush');
          localStorage.removeItem('notificacionesPushRegistradas');
          this._notificacionService.setShowNotifications(true);
        }

        this.message.set('Modificación realizada correctamente');
        setTimeout(() => this.message.set(''), 2500);
      } catch (e) {
        console.error(e);
        this.error.set('Error al realizar la modificación');
        // rollback simple si querés:
        if (disp) this.dispositivos = [disp, ...this.dispositivos];
      }
    });
  }

  // ====== Suscripción a notificaciones (web/nativo) ======
  async subscribeToNotifications(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        this.error.set('');
        // Limpio token FCM viejo (si hubiera)
        localStorage.removeItem('tokenFcmPush');

        // 1) Pido registro (ahora el servicio maneja web/nativo)
        const ok = await this._notificacionService.registerForPushNotifications();
        if (!ok) {
          this.error.set('Debe dar permisos para recibir notificaciones');
          return;
        }

        // 2) Enciendo listeners (el método interno puede no-op en web si ya están)
        if (IS_NATIVE) {
          await this._notificacionService.setupNotificationListeners();
        }

        // 3) Actualizo estado de permisos y UI
        await this.refreshPermissionStatus();

        // 4) Espero un toque a que el backend guarde el token y recargo lista
        await new Promise(r => setTimeout(r, 1200));
        await this.getDispositivosDelUsuario();

        // 5) Oculto “banner/botón” de activar notificaciones si lo usás
        this._notificacionService.setShowNotifications(false);
      } catch (e) {
        console.error('Error al suscribirse a las notificaciones:', e);
        this.error.set('Error al suscribirse a las notificaciones');
      }
    });
  }

  back(){
    this._location.back();
  }
}
